import { Icon, Menu } from "materia"
import { useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import type {
  AttachmentRef,
  MessageStatus,
  PeerProfile,
  SharedItemRef,
} from "../../types"
import {
  SharedProductCard,
  type SharedItem,
  type ShareCardState,
} from "../../../components/SharedProductCard"
import { PeerAvatar } from "../PeerAvatar"
import { linkify } from "../../../tools/linkify"
import { conversationTimestamp } from "../ConversationList/conversationTimestamp"
import { createLongPress } from "./longPress"
import { AttachmentImage, type ResolveAttachmentUrl } from "./AttachmentImage"
import s from "./MessageBubble.module.scss"

/**
 * So a story can open the menu: on a hover-capable device the trigger only
 * appears on hover, which a snapshot cannot do.
 */
export const MESSAGE_ACTIONS_TEST_ID = "message-actions-trigger"

type MessageBubbleProps = {
  body: string
  /** Ours sits on the right, in blue. */
  isOwn: boolean
  /** Deleted for everyone (FAME-507) — a stub, with the body withheld. */
  isRetracted: boolean
  /** A shared learning item (FAME-237), rendered as a card over the text body. */
  share?: SharedItemRef
  /** Its resolved metadata, once the batch lookup lands (FAME-453). */
  resolvedShare?: SharedItem
  /** The lookup is still in flight — the card shows its resolving state. */
  isShareResolving?: boolean
  /** The lookup failed — the card falls back to the sender's deep link (body). */
  shareResolveFailed?: boolean
  /** The image this message carries, shown above any caption. */
  attachment?: AttachmentRef
  /** Where an image message gets the URL to show its image. */
  resolveAttachmentUrl: ResolveAttachmentUrl
  /** Who sent it. Absent while their profile is still resolving. */
  author?: PeerProfile
  authorHandle?: string
  isRunStart?: boolean
  isRunEnd?: boolean
  isTightUnder?: boolean
  /** Came in while the thread was open, so it springs in (see `arrivals.ts`). */
  isArrival?: boolean
  /** Delivery of one of our own sends; absent on everything we received. */
  status?: MessageStatus
  /**
   * Absent when there is nothing to offer — which takes the whole menu with
   * it, rather than showing a disabled item (FAME-687).
   */
  onRetract?: () => void
  /** Start editing this message. Absent when it cannot be edited. */
  onEdit?: () => void
  /** Unix-ms of the latest Edit; absent on an unedited message. */
  editedAt?: number
  /** Try a failed send again. Present only when this message can be resent. */
  onResend?: () => void
  onOpenDm?: (handle: string) => void
  onOpenProduct?: (item: SharedItem) => void
}

export function MessageBubble({
  body,
  isOwn,
  isRetracted,
  share,
  resolvedShare,
  isShareResolving = false,
  shareResolveFailed = false,
  attachment,
  resolveAttachmentUrl,
  author,
  authorHandle,
  isRunStart = true,
  isRunEnd = true,
  isTightUnder = false,
  isArrival = false,
  status,
  onRetract,
  onEdit,
  editedAt,
  onResend,
  onOpenDm,
  onOpenProduct,
}: MessageBubbleProps) {
  const { t } = useTranslation()
  const row = useRef<HTMLDivElement>(null)
  // Opens the same menu the kebab does, by pressing the kebab.
  const longPress = useMemo(
    () =>
      createLongPress(() =>
        row.current
          ?.querySelector<HTMLButtonElement>(`.${s.message__actions}`)
          ?.click()
      ),
    []
  )
  useEffect(() => longPress.cancel, [longPress])

  const hasActions = Boolean(onRetract || onEdit)
  const canOpenDm = !isOwn && onOpenDm && authorHandle
  // A retracted card is a stub like any other deleted message — the item goes
  // with it, so the text path renders the "deleted" line instead.
  const shareState = share && !isRetracted ? toShareState() : undefined
  // An image without a caption needs no empty bubble under it.
  const hasTextBubble = isRetracted || body.length > 0 || !attachment

  function toShareState(): ShareCardState {
    if (isShareResolving) return { status: "resolving" }
    if (resolvedShare)
      return {
        status: "accessible",
        item: resolvedShare,
        onOpen: () => onOpenProduct?.(resolvedShare),
      }
    // The lookup failed, so access is unknown: offer the sender's deep link
    // (the body) rather than wrongly claiming no access (FAME-453).
    if (shareResolveFailed) return { status: "fallback", url: body }
    // Resolved but absent means the caller cannot open it.
    return { status: "noAccess" }
  }

  return (
    <div
      className={modifiers(s, "message", {
        isOwn,
        isRetracted,
        runContinued: !isRunStart,
        runEnd: isRunEnd,
        tight: isTightUnder,
        arrival: isArrival,
        hasActions,
      })}
      ref={row}
    >
      {/* Ours needs no face (FAME-815). Others' is hidden but keeps its width
          before a run's last message, so the bubbles stay aligned. */}
      {author && !isOwn && (
        <div className={s.message__avatar}>
          {isRunEnd && canOpenDm ? (
            <button
              type="button"
              className={s.message__avatarButton}
              onClick={() => onOpenDm(authorHandle)}
              aria-label={String(
                t("chat.MESSAGE_AUTHOR_DM_ARIA", { name: author.displayName })
              )}
            >
              <PeerAvatar {...author} size="small" />
            </button>
          ) : (
            <PeerAvatar {...author} size="small" />
          )}
        </div>
      )}

      <div
        className={s.message__body}
        {...(hasActions && {
          onPointerDown: longPress.onPointerDown,
          onPointerMove: longPress.onPointerMove,
          onPointerUp: longPress.onPointerUp,
          onPointerCancel: longPress.onPointerCancel,
          onContextMenu: longPress.onContextMenu,
        })}
      >
        {author && !isOwn && isRunStart && (
          <span className={s.message__author}>{author.displayName}</span>
        )}
        {/* A retracted image goes with its message: the text path below says so. */}
        {attachment && !isRetracted && (
          <AttachmentImage
            attachment={attachment}
            isOwn={isOwn}
            resolveUrl={resolveAttachmentUrl}
          />
        )}
        {shareState ? (
          <SharedProductCard isOwn={isOwn} state={shareState} />
        ) : (
          hasTextBubble && (
            <p className={s.message__bubble}>
              {isRetracted
                ? t("chat.MESSAGE_DELETED")
                : linkify(body).map((segment, i) =>
                    segment.type === "link" ? (
                      <a
                        // biome-ignore lint/suspicious/noArrayIndexKey: segments are positional; the body never reorders once rendered.
                        key={i}
                        href={segment.value}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                      >
                        {segment.value}
                      </a>
                    ) : (
                      segment.value
                    )
                  )}
            </p>
          )
        )}
        {editedAt !== undefined && !isRetracted && (
          <time
            className={s.message__time}
            dateTime={new Date(editedAt).toISOString()}
          >
            {t("chat.MESSAGE_EDITED", {
              time: conversationTimestamp(editedAt),
            })}
          </time>
        )}
        {/* A failed send must never look delivered, wherever it sits in a burst. */}
        {status === "failed" ? (
          <span className={s.message__failed}>
            {t("chat.MESSAGE_FAILED")}
            {onResend && (
              <button
                type="button"
                className={s.message__retry}
                onClick={onResend}
              >
                {t("chat.MESSAGE_RETRY")}
              </button>
            )}
          </span>
        ) : (
          status === "sending" && (
            <span className={s.message__time}>{t("chat.MESSAGE_SENDING")}</span>
          )
        )}
      </div>

      {hasActions && (
        <Menu>
          <Menu.Trigger asChild={true} dataTestId={MESSAGE_ACTIONS_TEST_ID}>
            <button
              className={s.message__actions}
              type="button"
              aria-label={String(t("chat.MESSAGE_ACTIONS_ARIA"))}
            >
              <Icon icon="Dots" />
            </button>
          </Menu.Trigger>

          {/* Opens away from the bubble, so it never covers the message. */}
          <Menu.Content
            anchor={isOwn ? "bottom end" : "bottom start"}
            className={s.message__menu}
          >
            {/* Passing a class replaces Menu.Item's own: the row is styled here. */}
            {onEdit && (
              <Menu.Item
                as="button"
                type="button"
                className={s.message__edit}
                onClick={onEdit}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 13 13"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M8.5 2.5l2 2M2 11l.5-2.5L9 2l2 2-6.5 6.5L2 11z"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.3"
                  />
                </svg>
                {t("chat.EDIT_MESSAGE")}
              </Menu.Item>
            )}
            {onRetract && (
              <Menu.Item
                as="button"
                type="button"
                className={s.message__delete}
                onClick={onRetract}
              >
                {/* Inline: Materia's Icon fills every shape, and this is an outline. */}
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 13 13"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2 3h9M4.5 3V2h4v1M5 6v4M8 6v4M3 3l.5 8h6l.5-8H3z"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.3"
                  />
                </svg>
                {t("chat.DELETE_MESSAGE")}
              </Menu.Item>
            )}
          </Menu.Content>
        </Menu>
      )}
    </div>
  )
}
